"use client";
import { AvailabilityComponent } from "@components/click-and-collect/AvailabilityComponent";
import { AvailabilityComponentCheckout } from "@components/click-and-collect/AvailabilityComponentCheckout";
import { checkoutPage, shoppingBagPage, storeLocatorPage } from "@lib/constants/pagePaths";
import { Box } from "@ui/components/layout/box/Box";
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
} from "@ui/components/overlay/modal/Modal";
import { useDisclosure } from "@ui/hooks/useDisclosure";
import { useContext, useEffect, useState } from "react";
import { Address } from "./Address";
import type { StoreData } from "./controller";
import { Controller } from "./controller";
import { formatStreet } from "./normalize-store";
import { WalkInHours } from "./walk-in-hours";
import { isEccoStore } from "./utils/isEccoStore";
import { activeSiteConfig } from "@config/site/site-config";
import { ShippingMethodTypes } from "@components/commerce/checkout/types";
import { usePathname } from "next/navigation";
import { use2xlBreakpoint } from "@ui/hooks/useBreakpoint";

interface StoreModalProps {
    selectedVariant?: any;
    onClose?: () => void;
    shippingMethodKey: string;
}

export const StoreModal = ({
    selectedVariant,
    onClose: onCloseParent,
    shippingMethodKey,
}: StoreModalProps) => {
    const { selectedStore, lastSelect, setLastSelect, isClickAndCollect } = useContext(Controller);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const pathname = usePathname();
    const isExtraLargeViewport = use2xlBreakpoint();
    const [store, setStore] = useState<StoreData>();
    const [localDate, setLocalDate] = useState<Date>();
    const { showParcelLockerId, hideParcelLockerWorkingHours, hideAvailabilityInStore } =
        activeSiteConfig;
    const isParcelShop = shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP;
    const showParcelLockerInfo = isParcelShop && showParcelLockerId;
    const hideParcelShopWorkingHours = isParcelShop && hideParcelLockerWorkingHours;
    const hideParcelShopAvailabilityComponent = isParcelShop && hideAvailabilityInStore;

    useEffect(() => {
        if (lastSelect !== 0) {
            setLastSelect(0);
            if (!isExtraLargeViewport) {
                onOpen();
            }
        }
    }, [lastSelect, onOpen, setLastSelect]);

    useEffect(() => {
        const store = selectedStore();
        if (store) {
            setStore(store);
            setLocalDate(new Date());
        }
    }, [selectedStore]);

    return isOpen ? (
        <Modal isOpen={isOpen} onClose={onClose} trapFocus={false} closeOnOverlayClick={false}>
            <ModalOverlay />
            <ModalContent
                mt={0}
                mb={0}
                alignSelf="flex-start"
                borderBottomLeftRadius="0"
                borderBottomRightRadius="0"
                marginInlineStart="auto"
            >
                <ModalCloseButton
                    _focus={{ border: "none" }}
                    top={0}
                    right={0}
                    sx={{
                        position: "absolute",
                        "--close-button-size": "48px",
                    }}
                />
                <ModalBody>
                    <Address
                        name={store?.name}
                        street={formatStreet(store)}
                        postalCode={store?.postalCode}
                        city={store?.city}
                        walkInHours={store?.walkInHours}
                        localDate={localDate}
                        latitude={store?.latitude}
                        longitude={store?.longitude}
                        isModal
                        isExpanded
                        isEccoStore={isEccoStore(store)}
                        storeId={store?.storeId}
                        showParcelLockerInfo={showParcelLockerInfo}
                        hideParcelShopWorkingHours={hideParcelShopWorkingHours}
                    />
                    {!hideParcelShopWorkingHours && (
                        <WalkInHours
                            localDate={localDate}
                            walkInHours={store?.walkInHours}
                            name={store?.name}
                            street={formatStreet(store)}
                            postalCode={store?.postalCode}
                            city={store?.city}
                            phone={store?.phone}
                            latitude={store?.latitude}
                            longitude={store?.longitude}
                            isModal
                        />
                    )}
                    {isClickAndCollect &&
                    !Object.values(checkoutPage).find((path) => pathname.includes(path)) &&
                    !Object.values(shoppingBagPage).find((path) => pathname.includes(path)) ? (
                        <Box bg="transparent" mt={4}>
                            <AvailabilityComponent
                                selectedVariant={selectedVariant}
                                onClose={onCloseParent}
                                store={store}
                            />
                        </Box>
                    ) : (
                        !Object.values(storeLocatorPage).find((path) =>
                            pathname.includes(path)
                        ) && (
                            <Box bg="transparent" mt={4}>
                                <AvailabilityComponentCheckout
                                    onClose={onCloseParent}
                                    store={store}
                                    hideParcelShopAvailabilityComponent={
                                        hideParcelShopAvailabilityComponent
                                    }
                                />
                            </Box>
                        )
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    ) : null;
};
